import { useState } from "react";
import {
  Link,
  NavLink,
  Outlet,
  useOutletContext,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  classifyViews,
  useEntitlements,
  type AgentDemo,
  type KnowledgeProvider,
  type QualitySummary,
  type ViewNavigation,
} from "@cloudox/api-client";
import { ErrorState, Logo, LoadingState, SectionNav, ThemeToggle, ViewSelect } from "@cloudox/ui";
import { AgentDemoPanel } from "@/components/agent/AgentDemoPanel";
import { useAgentDemo, useQuality, useViewNavigation } from "@/hooks/useKnowledgeData";
import { ASK_PARAM, askSearch, partitionViews, relatedPagePath, viewPath } from "@/lib/preview";

export interface KnowledgeContext {
  readonly provider: KnowledgeProvider;
  readonly nav: ViewNavigation;
  readonly quality: QualitySummary | null;
  readonly agentDemo: AgentDemo | null;
}

export function useKnowledgeContext(): KnowledgeContext {
  return useOutletContext<KnowledgeContext>();
}

/**
 * Public preview shell — the same Cloud Knowledge Workspace IA as the console:
 * a calm top bar (brand + theme + sign-in) and a view-first left rail (View
 * picker + section outline), rendering the selected Knowledge View.
 */
export function KnowledgeShell({ provider }: { readonly provider: KnowledgeProvider }) {
  const navState = useViewNavigation(provider);
  const qualityState = useQuality(provider);
  const agentDemoState = useAgentDemo(provider);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const nav = navState.status === "ready" ? navState.value : undefined;
  const quality = qualityState.status === "ready" ? qualityState.value : null;
  const agentDemo = agentDemoState.status === "ready" ? agentDemoState.value : null;

  // Presence, not truthiness: `?ask=` with no value opens the question list,
  // which is the state the rail link navigates to.
  const askedFor = searchParams.get(ASK_PARAM);
  const assistantOpen = askedFor !== null && agentDemo !== null;
  const closeAssistant = () => {
    const next = new URLSearchParams(searchParams);
    next.delete(ASK_PARAM);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col" style={{ backgroundColor: "var(--bg)" }}>
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <PreviewTopBar onOpenSidebar={() => setMobileOpen(true)} />

      <div className="flex flex-1">
        <div className="hidden w-72 shrink-0 lg:block">
          <div className="sticky top-14 h-[calc(100vh-3.5rem)]">
            {nav ? (
              <KnowledgeRail nav={nav} agentDemo={agentDemo} assistantOpen={assistantOpen} />
            ) : (
              <RailSkeleton />
            )}
          </div>
        </div>

        {mobileOpen && nav ? (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div
              className="absolute inset-0"
              style={{
                backgroundColor: "color-mix(in oklab, var(--color-ink-950) 60%, transparent)",
              }}
              onClick={() => setMobileOpen(false)}
              aria-hidden
            />
            <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw]">
              <KnowledgeRail
                nav={nav}
                agentDemo={agentDemo}
                assistantOpen={assistantOpen}
                onNavigate={() => setMobileOpen(false)}
              />
            </div>
          </div>
        ) : null}

        <main id="main" className="min-w-0 flex-1">
          <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-8 sm:py-12">
            {navState.status === "loading" ? (
              <LoadingState label="Loading knowledge views…" />
            ) : navState.status === "error" ? (
              <ErrorState error={toDocError(navState.error)} />
            ) : (
              <Outlet context={{ provider, nav: navState.value, quality, agentDemo }} />
            )}
          </div>
        </main>

        {assistantOpen && agentDemo ? (
          <AgentDemoPanel demo={agentDemo} questionKey={askedFor ?? ""} onClose={closeAssistant} />
        ) : null}
      </div>
    </div>
  );
}

function KnowledgeRail({
  nav,
  agentDemo,
  assistantOpen = false,
  onNavigate,
}: {
  readonly nav: ViewNavigation;
  readonly agentDemo: AgentDemo | null;
  readonly assistantOpen?: boolean;
  readonly onNavigate?: () => void;
}) {
  const { view, section } = useParams<{ view?: string; section?: string }>();
  const entitlements = useEntitlements();
  const { available, locked } = classifyViews(nav, entitlements);
  const { lenses, reference } = partitionViews(available);
  // No fallback to the first view: on the overview and the intelligence pages
  // no view is open, and highlighting one would tell the reader they are
  // somewhere they are not.
  const active = view ? available.find((v) => v.key === view) : undefined;
  const currentSection = active ? (section ?? active.sections[0]?.key) : undefined;
  // Reference is not an audience lens, so it is not in the select. Its section
  // list therefore has to sit under the Reference heading: listing a view's
  // sections beneath a select that reads "Select a view" tells the reader that
  // nothing is open while showing them the inside of something that is.
  const onReference = Boolean(active && reference && active.key === reference.key);
  const sectionNav =
    active && active.sections.length > 0 ? (
      <SectionNav
        view={active}
        {...(currentSection ? { currentSection } : {})}
        hrefFor={(sectionKey) => viewPath(active.key, sectionKey)}
        subHrefFor={(sectionKey, anchor) => `${viewPath(active.key, sectionKey)}#${anchor}`}
        {...(onNavigate ? { onNavigate } : {})}
      />
    ) : null;

  return (
    <aside
      className="flex h-full w-full flex-col overflow-y-auto px-3 py-4"
      style={{ backgroundColor: "var(--bg-sidebar)", borderRight: "1px solid var(--border)" }}
      aria-label="Knowledge views"
    >
      <RailLink to="/" onNavigate={onNavigate} end>
        Overview
      </RailLink>

      <RailHeading>Knowledge View</RailHeading>
      <ViewSelect
        views={lenses}
        lockedViews={locked}
        {...(active && !onReference ? { currentView: active.key } : {})}
        hrefFor={(key) => viewPath(key)}
        {...(onNavigate ? { onNavigate } : {})}
      />
      {onReference ? null : sectionNav}

      {reference ? (
        <>
          <RailHeading>Reference</RailHeading>
          <RailLink to={viewPath(reference.key)} onNavigate={onNavigate}>
            {reference.displayName} tables
          </RailLink>
          {onReference ? sectionNav : null}
        </>
      ) : null}

      {nav.relatedPages.length > 0 ? (
        <>
          <RailHeading>Intelligence layers</RailHeading>
          {nav.relatedPages.map((page) => (
            <RailLink key={page.key} to={relatedPagePath(page.key)} onNavigate={onNavigate}>
              {page.title}
            </RailLink>
          ))}
        </>
      ) : null}

      {agentDemo ? (
        <>
          <RailHeading>Assistant</RailHeading>
          {/* Not a `RailLink`: the target is a search string, so `NavLink`
              would compare the unchanged pathname and read as always active.
              The panel's own open state is the honest signal. */}
          <Link
            to={askSearch()}
            onClick={onNavigate}
            className="rounded-md px-2 py-1.5 text-sm"
            style={{
              textDecoration: "none",
              color: assistantOpen ? "var(--accent)" : "var(--fg-muted)",
              backgroundColor: assistantOpen ? "var(--accent-muted)" : "transparent",
              fontWeight: assistantOpen ? 600 : 400,
            }}
          >
            Ask the CDX Agent
          </Link>
        </>
      ) : null}
    </aside>
  );
}

function RailHeading({ children }: { readonly children: React.ReactNode }) {
  return (
    <p
      className="px-1 pb-1.5 pt-4 text-[11px] font-semibold uppercase tracking-[0.12em]"
      style={{ color: "var(--fg-subtle)" }}
    >
      {children}
    </p>
  );
}

function RailLink({
  to,
  children,
  onNavigate,
  end = false,
}: {
  readonly to: string;
  readonly children: React.ReactNode;
  readonly onNavigate?: (() => void) | undefined;
  readonly end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className="rounded-md px-2 py-1.5 text-sm"
      style={({ isActive }) => ({
        textDecoration: "none",
        color: isActive ? "var(--accent)" : "var(--fg-muted)",
        backgroundColor: isActive ? "var(--accent-muted)" : "transparent",
        fontWeight: isActive ? 600 : 400,
      })}
    >
      {children}
    </NavLink>
  );
}

function PreviewTopBar({ onOpenSidebar }: { readonly onOpenSidebar: () => void }) {
  return (
    <header
      className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 px-3 sm:px-5"
      style={{
        backgroundColor: "color-mix(in oklab, var(--bg) 85%, transparent)",
        borderBottom: "1px solid var(--border)",
        backdropFilter: "blur(8px)",
      }}
    >
      <div className="flex min-w-0 items-center gap-2">
        <button
          type="button"
          className="btn btn-ghost lg:hidden"
          aria-label="Open navigation"
          onClick={onOpenSidebar}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden
          >
            <path d="M3 6h18M3 12h18M3 18h18" />
          </svg>
        </button>
        <Link to="/" className="flex items-center gap-2" aria-label="CloudoX home">
          <Logo height={24} />
        </Link>
        {/* Visible on every route, because the disclosure the published Markdown
            carries is dropped when a composed view is split into sections.
            The label is drawn in `--fg` rather than `--warning`: at this size
            the warning hue over its own tint measures 4.28:1, and a caution a
            reader cannot read is not a caution. */}
        <span
          className="rounded-md border px-1.5 py-0.5 text-[11px] font-medium"
          style={{
            borderColor: "color-mix(in oklab, var(--warning) 45%, transparent)",
            backgroundColor: "color-mix(in oklab, var(--warning) 12%, transparent)",
            color: "var(--fg)",
          }}
        >
          Sanitized demo
        </span>
      </div>

      <div className="flex items-center gap-1">
        <a
          href="https://cloudox.io"
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-ghost hidden sm:inline-flex"
        >
          <span className="text-xs">cloudox.io</span>
        </a>
        <ThemeToggle />
      </div>
    </header>
  );
}

function RailSkeleton() {
  return (
    <aside
      className="h-full w-full"
      style={{ backgroundColor: "var(--bg-sidebar)", borderRight: "1px solid var(--border)" }}
      aria-hidden
    />
  );
}

function toDocError(error: unknown): never {
  // ErrorState expects a DocumentProviderError; the provider already throws one.
  return error as never;
}
